import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Driver, SystemConfig } from '../../types';
import { X, TrendingUp, DollarSign, Clock, Zap, Map } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { calculateDriverDailyEarnings } from '../../services/driverCompensation';

interface Props {
    driver: Driver;
    config: SystemConfig;
    isOpen: boolean;
    onClose: () => void;
    todaysDeliveries?: number;
}

export const DriverPerformanceModal: React.FC<Props> = ({ driver, config, isOpen, onClose, todaysDeliveries }) => {
    if (!isOpen) return null;

    const totalDeliveries = todaysDeliveries ?? 0;
    const { dropFee, totalDropEarnings, totalDailyEarnings, totalEarnings } = calculateDriverDailyEarnings(config, totalDeliveries);

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                />

                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    className="relative w-full max-w-md bg-sal-gunmetal border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
                >
                    <div className="bg-sal-gunmetal-light/50 p-6 border-b border-white/5 flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-black text-white uppercase tracking-tighter flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-sal-cyber-teal" /> Meu Desempenho
                            </h2>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">
                                {format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR })}
                            </p>
                        </div>
                        <button onClick={onClose} className="p-2 bg-white/5 rounded-xl hover:bg-white/10 active:scale-95 transition-all text-slate-400">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Financials */}
                        <div className="bg-gradient-to-br from-emerald-500/10 to-transparent p-5 rounded-2xl border border-emerald-500/20 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                <DollarSign className="w-24 h-24 text-emerald-500" />
                            </div>
                            <div className="relative">
                                <span className="text-xs font-black text-emerald-400 uppercase tracking-widest block mb-2">Total a Receber (Hoje)</span>
                                <div className="text-4xl font-black text-white tracking-tighter flex items-baseline gap-1">
                                    <span className="text-xl text-emerald-500">R$</span>
                                    {totalEarnings.toFixed(2)}
                                </div>
                                <div className="mt-4 space-y-2">
                                    <div className="flex justify-between text-sm border-b border-white/5 pb-2">
                                        <span className="text-slate-400">Diária de Serviço</span>
                                        <span className="text-white font-mono">R$ {totalDailyEarnings.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-400">Taxa p/ Entrega ({dropFee.toFixed(2)} x {totalDeliveries})</span>
                                        <span className="text-white font-mono">R$ {totalDropEarnings.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Metrics Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                                <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-4 h-4 text-blue-400" />
                                    <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Tempo na Porta</span>
                                </div>
                                <div className="text-3xl font-black text-white">
                                    {driver.avgServiceTimeMinutes ? driver.avgServiceTimeMinutes.toFixed(1) : '-'} <span className="text-sm text-slate-500 font-bold">min</span>
                                </div>
                            </div>

                            <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                                <div className="flex items-center gap-2 mb-2">
                                    <Zap className="w-4 h-4 text-yellow-400" />
                                    <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Velocidade Média</span>
                                </div>
                                <div className="text-3xl font-black text-white">
                                    {driver.avgSpeedKmH ? driver.avgSpeedKmH.toFixed(0) : '-'} <span className="text-sm text-slate-500 font-bold">km/h</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white/5 p-4 rounded-2xl border border-white/5 flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <Map className="w-4 h-4 text-purple-400" />
                                    <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Fator de Velocidade</span>
                                </div>
                                <div className="text-xs text-slate-400">Vs. Estimativa Google Maps</div>
                            </div>
                            <div className="text-right">
                                <div className={`text-3xl font-black ${(driver.speedFactor || 1) >= 1 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {driver.speedFactor ? driver.speedFactor.toFixed(2) : '1.00'}x
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
